import { useCallback, useRef, useState } from 'react';
import SignaturePad from 'signature_pad';
import { toast } from 'sonner';

// Requester fields (sender_fillable) render as plain <Input>s on every send
// surface that uses this hook, not the full PDF-canvas field renderer the
// candidate-facing completion page uses — but they should still get the
// right native keyboard/picker per field_type (a browser date input opens
// its own calendar; a number input rejects non-numeric keystrokes) instead
// of every type falling through to text.
export const SENDER_FIELD_INPUT_TYPE = { date: 'date', number: 'number', email: 'email', phone: 'tel' };

/**
 * useSenderFillableFields — Requester/Admin ("sender_fillable") field
 * capture, including signature-pad support, shared by every send surface
 * that needs it (currently: the Bullhorn embed tab and the HyreDocs
 * portal's packet-send page).
 *
 * `activeTemplates` is the list of currently-relevant templates, each
 * shaped `{ id, name, fields }` (the same shape `GET /api/templates`
 * already returns, with each field carrying `sender_fillable`/`field_type`).
 * Recompute and pass a fresh array whenever the caller's active
 * selection changes (selected template(s), selected packet's templates,
 * etc.) — this hook does no fetching of its own.
 *
 * Values are keyed by `${templateId}::${fieldId}` (see `senderFieldKey`)
 * since the same field id can exist on different templates when more
 * than one is active in a combined send — `buildPrefilledValues` strips
 * that prefix back off when constructing a request body for one
 * particular set of template ids.
 */
export function useSenderFillableFields(activeTemplates) {
  const [senderFieldValues, setSenderFieldValues] = useState({});
  const senderFieldKey = (templateId, fieldId) => `${templateId}::${fieldId}`;
  const [signingKey, setSigningKey] = useState(null); // senderFieldKey currently being signed
  const [signatureMode, setSignatureMode] = useState('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const signaturePadRef = useRef(null);

  // Callback ref (not a useEffect) — the signing Dialog renders via a Radix
  // portal, so a plain useEffect keyed on state can fire before the canvas
  // is actually attached to the DOM, leaving SignaturePad uninitialized and
  // drawing silently doing nothing. A callback ref fires exactly when the
  // node mounts/unmounts, so it can't miss the timing.
  const initSignatureCanvas = useCallback((canvasEl) => {
    signaturePadRef.current = canvasEl
      ? new SignaturePad(canvasEl, { backgroundColor: 'rgb(255, 255, 255)', penColor: 'rgb(0, 0, 0)' })
      : null;
  }, []);

  const saveSignature = () => {
    if (signatureMode === 'type') {
      if (!typedSignature.trim()) {
        toast.error('Please type your name');
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = 450;
      canvas.height = 150;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 450, 150);
      ctx.fillStyle = 'black';
      ctx.font = 'italic 48px "Georgia", "Times New Roman", serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedSignature.trim(), 225, 75);
      setSenderFieldValues(prev => ({ ...prev, [signingKey]: canvas.toDataURL() }));
      setSigningKey(null);
      setTypedSignature('');
      setSignatureMode('draw');
    } else if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      setSenderFieldValues(prev => ({ ...prev, [signingKey]: signaturePadRef.current.toDataURL() }));
      setSigningKey(null);
    } else {
      toast.error('Please draw your signature first');
    }
  };

  // Flat list of {templateId, templateName, field} for every sender_fillable
  // field across the currently-active template(s).
  const senderFillableFields = activeTemplates.flatMap((tpl) =>
    (tpl?.fields || [])
      .filter((f) => f.sender_fillable)
      .map((field) => ({ templateId: tpl.id, templateName: tpl.name, field })));
  const unfilledSenderFields = senderFillableFields.filter(({ templateId, field }) => {
    const v = senderFieldValues[senderFieldKey(templateId, field.id)];
    if (field.field_type === 'signature') return typeof v !== 'string' || !v.startsWith('data:image/');
    return v == null || String(v).trim() === '';
  });

  // Build the `prefilled_values` payload (plain field-id keys, no
  // templateId:: prefix) for whichever template id(s) a particular send/
  // preview request covers — field ids are UUIDs, unique across every
  // template, so scoping to just the relevant template(s) only matters
  // for not leaking an unrelated template's sender values into a request
  // that doesn't concern it.
  const buildPrefilledValues = (templateIds) => {
    const ids = new Set(Array.isArray(templateIds) ? templateIds : [templateIds]);
    return Object.fromEntries(
      Object.entries(senderFieldValues)
        .filter(([key]) => ids.has(key.slice(0, key.indexOf('::'))))
        .map(([key, value]) => [key.slice(key.indexOf('::') + 2), value]),
    );
  };

  return {
    senderFieldValues, setSenderFieldValues, senderFieldKey,
    signingKey, setSigningKey,
    signatureMode, setSignatureMode,
    typedSignature, setTypedSignature,
    signaturePadRef, initSignatureCanvas, saveSignature,
    senderFillableFields, unfilledSenderFields,
    buildPrefilledValues,
  };
}
